import {
    RiCheckLine, RiEdit2Line, RiLayoutColumnLine, RiProgress3Line,
} from "@remixicon/react";
import React, {
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    ChatStatus,
    GetTicketStateDocument,
    GetTicketStatesListDocument,
    TicketState,
    useGetTicketStateQuery,
    useGetTicketStatesListQuery,
    useSaveTicketStateMutation,
} from "../../../../__generated__/api";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import ChatStatusPicker, { STATUS_TEXT } from "../../../../components/pickers/chat-status/ChatStatus";
import Badge from "../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import createTableContainer from "../../../../components/theme/table";
import Toggle from "../../../../components/theme/toggle/Toggle";

interface TicketStateWithStatus extends TicketState {
    chatStatusList: string
}
const TableContainer = createTableContainer<TicketStateWithStatus>();

export function useCreateView() {
    const [id, setViewId] = useState<string | undefined>(undefined);
    const { data, loading: isLoading } = useGetTicketStateQuery({
        skip: !id,
        variables: {
            id,
        },
    });

    const [save] = useSaveTicketStateMutation();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: id ? "Edit Column" : "Add Column",
        isLoading,
        onHide: () => {
            setViewId(undefined);
            formTracker.reset();
        },
        onSave: async () => {
            await save({
                refetchQueries: [
                    { query: GetTicketStatesListDocument },
                    { query: GetTicketStateDocument, variables: { id } },
                ],
                variables: {
                    id,
                    state: {
                        name: formTracker.properties.name.value,
                        description: formTracker.properties.description.value,
                        chatStatuses: formTracker.properties.chatStatuses.value,
                        ticketStatuses: data?.ticketState?.ticketStatuses ?? [],
                        isDefault: formTracker.properties.isDefault.value,
                    },
                },
            });
            setViewId(undefined);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property("name", () => data?.ticketState?.name ?? "")
                .property("description", () => data?.ticketState?.description ?? "")
                .property("chatStatuses", () => data?.ticketState?.chatStatuses ?? [])
                .property("isDefault", () => data?.ticketState?.isDefault),
            dependencies: [data?.ticketState],
        },
        body: tracker => (
            <>
                <FormGroup
                    label="Column Name"
                    size="medium"
                    required
                    value={tracker.properties.name.value}
                    onChange={evt => tracker.properties.name.setValue(evt.target.value)}
                />
                <FormGroup
                    label="Description"
                    size="medium"
                    display="textarea"
                    isOptional
                    value={tracker.properties.description.value}
                    onChange={evt => tracker.properties.description.setValue(evt.target.value)}
                />
                <FormGroup
                    label="States"
                    isRequired
                    helpText="Which ticket statuses does this column manage?"
                    helpDisplay="icon"
                >
                    <ChatStatusPicker
                        isMulti
                        placeholder="Please pick an ticket state"
                        onChange={tracker.properties.chatStatuses.setValue}
                        value={tracker.properties.chatStatuses.value}
                    />
                </FormGroup>
                <FormGroup label="Default for new Dashboards" isRequired>
                    <Toggle
                        textVariant="secondary"
                        text="If enabled, this column will automatically be added to the views for default users."
                        onChange={event => tracker.properties.isDefault.setValue(event.target.checked)}
                        checked={tracker.properties.isDefault.value ?? false}
                        labelClassName="d-flex"
                        className="flex-shrink-0"
                    />
                </FormGroup>
            </>
        ),
    });

    useEffect(() => {
        if (id) {
            setOpen(true);
        }
    }, [id]);

    return { element, setOpen, setViewId };
}

export default function ColumnsTable() {
    const { data, error, loading: isLoading } = useGetTicketStatesListQuery();

    const statusData = useMemo(() => data?.ticketStates?.map(state => ({
        ...state,
        chatStatusList: state.chatStatuses?.map(current => STATUS_TEXT[current]).join(", "),
    }) as TicketStateWithStatus), [data]);

    const { element: drawerElement, setOpen, setViewId } = useCreateView();

    return (
        <>
            <TableContainer data={statusData} isLoading={isLoading} error={error}>
                <TableContainer.Empty>
                    <TableContainer.Empty.Default
                        text="Columns help you manage the statuses of tickets in your dashboard."
                        title="Create an Column"
                        icon={<RiLayoutColumnLine />}
                        onClick={() => setOpen(true)}
                        buttonText="Add Column"
                    />
                </TableContainer.Empty>
                <TableContainer.TableControls>
                    <TableContainer.TableControls.Search
                        search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                                    || item.description?.toLocaleLowerCase().includes(query)
                                                    || item.chatStatuses.some(status => status.toLowerCase().includes(query))}
                    />
                    <TableContainer.TableControls.Seperator />
                    <TableContainer.TableControls.Filters />

                    <TableContainer.TableControls.Filter
                        defaultValue={[] as ChatStatus[]}
                        deserialize={TableContainer.TableControls.Filter.Serializers.deserializeArray}
                        serialize={TableContainer.TableControls.Filter.Serializers.serializeArray}
                        filterKey="states"
                        onFilter={(item, selected) => item.chatStatuses?.some(perm => selected.includes(perm))}
                    >
                        {(currentValue, setValue) => (
                            <ChatStatusPicker
                                toggleText="Status"
                                className=""
                                isMulti
                                icon={<RiProgress3Line />}
                                value={currentValue}
                                onChange={setValue}
                                shouldShowDropdown
                            />
                        )}
                    </TableContainer.TableControls.Filter>
                    <TableContainer.TableControls.Filter
                        defaultValue={undefined as boolean | undefined}
                        deserialize={TableContainer.TableControls.Filter.Serializers.deserializeString<boolean>}
                        serialize={TableContainer.TableControls.Filter.Serializers.serializeString<boolean>}
                        filterKey="isDefault"
                        onFilter={(item, selected) => item.isDefault === selected}
                    >
                        {(currentValue, setValue) => (
                            <BooleanSelect
                                toggleText="Default Column"
                                icon={<RiCheckLine />}
                                value={currentValue}
                                isRequired
                                onChange={setValue}
                                shouldShowDropdown
                            />
                        )}
                    </TableContainer.TableControls.Filter>

                    <TableContainer.TableControls.SortBy />
                    <TableContainer.TableControls.Create text="Add Column" onClick={() => setOpen(true)} />
                </TableContainer.TableControls>
                <TableContainer.Table>
                    <TableContainer.TableValue
                        columnKey="name"
                        title="Column Name"
                        sortBy={item => item.name?.toLowerCase()}
                    >
                        {item => item.name}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="description"
                        title="Description"
                        sortBy={item => item.description?.toLowerCase()}
                    >
                        {item => item.description}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="statuses"
                        title="Status"
                        sortBy={item => item.chatStatusList}
                    >
                        {item => item.chatStatuses.map(status => (
                            <Badge className="m3e-1" variant="primary">{STATUS_TEXT[status]}</Badge>
                        ))}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="isDefault"
                        title="Default Column"
                        sortBy={item => item.isDefault}
                    >
                        {item => (item.isDefault ? <Icon variant="secondary" icon={<RiCheckLine />} /> : undefined)}
                    </TableContainer.TableValue>

                    <TableContainer.Action
                        icon={<RiEdit2Line />}
                        altText="Edit Column"
                        onClick={column => {
                            setViewId(column.id);
                        }}
                    />

                </TableContainer.Table>
            </TableContainer>
            {drawerElement}
        </>
    );
}
